import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtCurrency } from "@/lib/constants";
import { CircleDollarSign, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";

const monthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function BookedRevenueCard() {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthStr(new Date()));
  const { data } = useQuery({
    queryKey: ["booked-revenue", month],
    queryFn: async () => (await api.get("/project/booked-revenue", { params: { month } })).data,
    refetchInterval: 60000,
    placeholderData: (prev) => prev,
  });
  const [y, m] = month.split("-").map(Number);
  const fallbackLabel = new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const shift = (n) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthStr(new Date(y, m - 1 + n, 1)));
  };
  const jobs = data?.jobs || [];
  return (
    <div className="border border-slate-200 bg-white industrial-shadow rounded-sm" data-testid="booked-revenue-card">
      <div className="flex items-center justify-between px-4 py-3 flex-wrap gap-2">
        <button type="button" onClick={() => setOpen(!open)} className="stamp-label text-slate-600 flex items-center gap-1.5" data-testid="booked-revenue-toggle">
          <CircleDollarSign size={13} className="text-emerald-600" /> Booked revenue — jobs finishing this month
          {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => shift(-1)} className="w-6 h-6 flex items-center justify-center border border-slate-300 rounded-sm text-slate-500 hover:text-slate-900 hover:border-slate-400" data-testid="booked-revenue-prev">
            <ChevronLeft size={13} />
          </button>
          <span className="text-xs font-bold text-slate-900 min-w-[110px] text-center" data-testid="booked-revenue-month">{data?.label || fallbackLabel}</span>
          <button type="button" onClick={() => shift(1)} className="w-6 h-6 flex items-center justify-center border border-slate-300 rounded-sm text-slate-500 hover:text-slate-900 hover:border-slate-400" data-testid="booked-revenue-next">
            <ChevronRight size={13} />
          </button>
          <span className="font-mono font-bold text-sm text-emerald-700" data-testid="booked-revenue-total">{fmtCurrency(data?.total || 0)}</span>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4">
          {jobs.length === 0 ? (
            <p className="text-xs text-slate-400">No jobs scheduled to finish in {data?.label}.</p>
          ) : (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="booked-revenue-jobs">
              {jobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between px-2.5 py-1.5 text-[11px]" data-testid={`booked-revenue-job-${j.job_number}`}>
                  <span className="text-slate-700 truncate">
                    <b className="font-mono text-emerald-800">{j.job_number}</b> · {j.title}
                    <span className="text-slate-400"> · finishes {j.scheduled_end ? new Date(`${j.scheduled_end}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }) : "—"}</span>
                  </span>
                  <span className="font-mono text-slate-900 font-semibold shrink-0 ml-3">{fmtCurrency(j.value)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-slate-400 pt-2">
            A job counts in the month its install is scheduled to finish (current scheduled end date) — value is the accepted quote total. Moves automatically when the schedule changes.
          </p>
        </div>
      )}
    </div>
  );
}
